// ?===============================
// ?=====  3rd party Imports  =====
// ?===============================

import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'gatsby-link'
import { Flex, NavLink, Box, Link as ExternalLink } from '@theme-ui/components'
import { nanoid } from 'nanoid'
import slugify from 'slugify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPhone } from '@fortawesome/free-solid-svg-icons'

// ?===================================
// ?===  Local Components Imports  ====
// ?===================================

import CloudinaryImage from '../../../../General/CloudinaryImage'
import LocationSwitcher from '../../../../LocationSwitchers/v1/LocationSwitcher'
import SocialIcons from '../../../../General/SocialIcons/SocialIconsV1'
import DropdownNavItem from '../dropdownComponents/DropdownNavItemV1'

// ?===================================
// ?====  Local Function Imports  =====
// ?===================================
import checkIfExternalLink from '../../../../../helpers/checkIfExternalLink'
import combineSocialLinks from '../../../../../helpers/combineSocialLinks'
import multiLocationLink from '../../../../../helpers/multiLocationLink'

// ?===================================
// ?==========  Component  ============
// ?===================================

export default function Index(props) {
  // ?==========================
  // ?====== Destructing  ======
  // ?==========================
  const {
    businessData,
    configData,
    // customPageName,
    // gonationID
    isMultiLocationSite,
    links,
    locationIndex,
    // pageTitle,
    locations
    // navLinks,
    // pageComponents,
    // pageObjectKey
  } = props

  const {
    avatar: { imageBaseUrl, imagePrefix },
    city,
    slug,
    phone,
    links: socialLinks
  } = businessData

  const { options, variantName } = configData.navigation

  // * ===== Component Options =====
  const { containerStyles, socialLinksHidden, phoneHidden, linkedin, pinterest } = options

  // ?===========================
  // ?===== Component State =====
  // ?===========================
  const [navOpen, toggleNav] = useState(false)
  const [navBackground, setNavBackground] = useState(false)

  // ?==============================
  // ?=====  Variable Creation  ====
  // ?==============================

  const variant = variantName ? variantName : 'navigationV3'
  const styles = containerStyles ? JSON.parse(containerStyles) : {}
  const navRef = useRef()
  navRef.current = navBackground
  const configuredSocialLinks =
    linkedin || pinterest
      ? combineSocialLinks(socialLinks, linkedin?.[locationIndex], pinterest?.[locationIndex])
      : socialLinks

  // ?=============================================
  // ?==============   Functions   ================
  // ?=============================================

  // ?============================================
  // ?===========   ON/OFF MOUNTING   ============
  // ?============================================

  useEffect(() => {
    const handleScroll = () => {
      const show = window.scrollY > 70
      if (navRef.current !== show) {
        setNavBackground(show)
      }
    }
    document.addEventListener('scroll', handleScroll)
    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // ?=========================
  // ?===== JSX rendering =====
  // ?=========================

  return (
    <Box sx={styles}>
      <Flex
        variant={`${variant}.${navBackground ? 'containerScrolled' : 'container'}`}
        as="nav"
        className={`${navBackground ? 'containerScrolled' : 'container'}`}
      >
        <Flex
          variant={`${variant}.${navBackground ? 'logoScrolled' : 'logo'}`}
          className={`${navBackground ? 'logoScrolled' : 'logo'}`}
        >
          <Link to={isMultiLocationSite ? `/${slugify(city, { lower: true })}` : `/`}>
            <CloudinaryImage imageBaseUrl={imageBaseUrl} imagePrefix={imagePrefix} />
          </Link>
        </Flex>
        <Flex
          variant={`${variant}.${!navOpen ? 'hamburger' : 'hamburgerOpen'}`}
          className={`${!navOpen ? 'hamburger' : 'hamburgerOpen'}`}
          onClick={() => {
            toggleNav(!navOpen)
          }}
        >
          <div></div>
          {!navOpen ? <div></div> : ''}
          <div></div>
        </Flex>

        <Flex
          variant={`${variant}.navMenu`}
          className="navMenu"
          style={{
            left: `${navOpen ? '0%' : '-150%'}` // moves mobile nav into position
          }}
        >
          {links.map(page => {
            const { ctaName, ctaLink, className, dropdownLinks } = page

            // if there is a list of dropdownlinks then use the dropdown component
            if (dropdownLinks) {
              return (
                <DropdownNavItem
                  navLinks={page}
                  variantName={variant}
                  isMultiLocationSite={isMultiLocationSite}
                  city={city}
                  key={nanoid()}
                />
              )
            } else {
              return (
                <NavLink
                  as="li"
                  className={`
                    navItem
                    ${slugify(ctaName, { lower: true })}
                    ${className ? className : ''}`}
                  variant={`${variant}.navItem`}
                  key={nanoid()}
                  sx={{
                    textDecoration: `${
                      props.location.pathname === `/${ctaLink}` // replaces activeclassname if on this page apply this style
                        ? 'underline'
                        : 'none'
                    }`
                  }}
                >
                  {checkIfExternalLink(page.ctaLink) ? (
                    <a href={ctaLink} target="_blank" rel="noreferrer">
                      {ctaName}
                    </a>
                  ) : (
                    <Link to={isMultiLocationSite ? multiLocationLink(city, ctaLink) : `/${ctaLink}`}>{ctaName}</Link>
                  )}
                </NavLink>
              )
            }
          })}

          {phoneHidden ? (
            ''
          ) : (
            <Flex variant={`${variant}.phoneContainer`} as="li" className="phoneContainer">
              <ExternalLink href={`tel:${phone}`} target="_blank" rel="noreferrer">
                <FontAwesomeIcon icon={faPhone} /> {phone}
              </ExternalLink>
            </Flex>
          )}

          {socialLinksHidden ? (
            ''
          ) : (
            <Flex variant={`${variant}.socialIconsContainer`} as="li" className="socialIconsContainer">
              <SocialIcons links={configuredSocialLinks} slug={slug} />
            </Flex>
          )}

          {isMultiLocationSite && (
            <Flex variant={`${variant}.locationSwitcher`} className="locationSwitcher">
              <LocationSwitcher businessData={businessData} locations={locations} />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Box>
  )
}
